import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, GripVertical } from 'lucide-react';
import { API } from '../../App';
import { convertGoogleDriveLink } from '../../utils/imageUtils';

const AdminCategoriesPage = ({ adminToken }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    description: '',
    sort_order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', image: '', description: '', sort_order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      image: category.image || '',
      description: category.description || '',
      sort_order: category.sort_order || 0
    });
    setEditingId(category.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
      if (editingId) {
        await axios.put(`${API}/admin/categories/${editingId}`, formData, { headers });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, formData, { headers });
        toast.success('Category created');
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"? Products in this category won't be deleted.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20">Loading...</div>;
  }

  return (
    <div className="py-8" data-testid="admin-categories-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-heading font-bold">Categories</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-primary text-primary-foreground px-6 py-3 flex items-center space-x-2 hover:bg-primary/90 transition-colors"
          data-testid="add-category-btn"
        >
          <Plus size={20} />
          <span>Add Category</span>
        </button>
      </div>

      {/* Category Form Modal */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-white w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto"
          >
            <button onClick={resetForm} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-heading font-bold mb-6">
              {editingId ? 'Edit Category' : 'Add Category'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                  placeholder="e.g., Bridal, Festive, Party Wear"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                  placeholder="Image URL or Google Drive sharing link"
                  data-testid="category-image-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Supports direct URLs and Google Drive links</p>
                {formData.image && (
                  <div className="mt-2 border border-border p-2">
                    <p className="text-xs text-muted-foreground mb-1">Preview:</p>
                    <img
                      src={convertGoogleDriveLink(formData.image)}
                      alt="Preview"
                      className="h-24 w-auto object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                  rows="2"
                  placeholder="Short description of this category"
                  data-testid="category-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sort Order</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                  placeholder="0"
                  data-testid="category-sort-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first</p>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-3 font-medium hover:bg-primary/90 transition-colors"
                data-testid="category-submit-btn"
              >
                {editingId ? 'Update Category' : 'Create Category'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Categories Grid */}
      {categories.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <p className="text-lg">No categories yet</p>
          <p className="text-sm mt-2">Add your first category to organize products</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border border-border overflow-hidden group"
              data-testid={`category-card-${index}`}
            >
              {category.image ? (
                <div className="h-40 overflow-hidden">
                  <img
                    src={convertGoogleDriveLink(category.image)}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ) : (
                <div className="h-40 bg-accent flex items-center justify-center">
                  <GripVertical size={40} className="text-muted-foreground/30" />
                </div>
              )}
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-heading font-semibold">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">Sort: {category.sort_order}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors"
                      data-testid={`edit-category-${index}`}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id, category.name)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                      data-testid={`delete-category-${index}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCategoriesPage;
